import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNarrative } from "../hooks/useNarrative";
import { useTournament } from "../contexts/TournamentContext";
import { useTheme } from "../contexts/ThemeContext";
import { Token } from "../types/tournament";

interface DefeatedToken {
  token: Token;
  round: string;
  liquidityAbsorbed: number;
}

interface ChampionViewProps {
  onReturnHome?: () => void;
}

export const ChampionView: React.FC<ChampionViewProps> = ({ onReturnHome }) => {
  const narrative = useNarrative();
  const { tournament } = useTournament();
  const { colors } = useTheme();
  const [showCrown, setShowCrown] = useState(false);
  const [showDefeated, setShowDefeated] = useState(false);
  const [showParticles, setShowParticles] = useState(false);

  const finalsMatch = tournament.rounds[0]?.matches[0];
  const winner = finalsMatch?.tokenA; // Shogunate wins in demo

  // Get only the opponents that the winner DIRECTLY defeated
  const getDefeatedTokens = (): DefeatedToken[] => {
    if (!winner) return [];

    const defeated: DefeatedToken[] = [];

    // Track the winner through each round - they can only defeat ONE opponent per round
    tournament.rounds.forEach((round, roundIndex) => {
      // Find the match where our winner participated
      const winnerMatch = round.matches.find((match) => {
        const hasWinner =
          match.tokenA?.name === winner.name ||
          match.tokenB?.name === winner.name;

        // For finals (round 0), we know the winner even if match.winner isn't set
        if (roundIndex === 0 && hasWinner) {
          return true;
        }

        // For other rounds, check that they won
        return hasWinner && match.winner?.name === winner.name;
      });

      if (winnerMatch) {
        // Get the loser from this specific match
        const loser =
          winnerMatch.tokenA?.name === winner.name
            ? winnerMatch.tokenB
            : winnerMatch.tokenA;
        if (loser) {
          // The liquidity absorbed is the loser's liquidity at the time of defeat
          defeated.push({
            token: loser,
            round: round.name,
            liquidityAbsorbed: loser.liquidity,
          });
        }
      }
    });

    return defeated.reverse(); // Show from first round to finals
  };

  const defeatedTokens = getDefeatedTokens();

  // Calculate the actual total absorbed (sum of all defeated tokens' liquidity)
  const totalAbsorbed = defeatedTokens.reduce(
    (sum, d) => sum + d.liquidityAbsorbed,
    0
  );

  // Format numbers with proper decimal places
  const formatLiquidity = (value: number) => {
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    // Trigger animations with nice timing
    setTimeout(() => {
      setShowCrown(true);
      setShowParticles(true);
    }, 500);

    setTimeout(() => setShowDefeated(true), 1200);
  }, []);

  if (!winner) return null;

  // Generate particles for celebration effect - fewer on mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const particleCount = isMobile ? 0 : 25; // No particles on mobile for performance
  const particles = Array.from({ length: particleCount }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
    size: Math.random() > 0.5 ? "w-1 h-1" : "w-2 h-2",
  }));

  return (
    <div className="relative h-screen bg-black overflow-hidden">
      {/* Background effects layer - fixed position */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Animated gradient background */}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              "radial-gradient(circle at center, rgba(251,191,36,0.15) 0%, transparent 60%)",
              "radial-gradient(circle at center, rgba(168,85,247,0.15) 0%, transparent 60%)",
              "radial-gradient(circle at center, rgba(251,191,36,0.15) 0%, transparent 60%)",
            ],
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(${colors.grid.lineColorHex}30 1px, transparent 1px), 
                             linear-gradient(90deg, ${colors.grid.lineColorHex}30 1px, transparent 1px)`,
            backgroundSize: "3.125rem 3.125rem",
          }}
        />

        {/* Victory particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className={`absolute ${particle.size} bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full hidden sm:block`}
            style={{ left: `${particle.x}%`, top: `${particle.y}%` }}
            initial={{ opacity: 0, scale: 0 }}
            animate={
              showParticles
                ? {
                    opacity: [0, 1, 0],
                    scale: [0, 1.5, 0],
                    y: [-20, -150],
                  }
                : {
                    opacity: 0,
                    scale: 0,
                  }
            }
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: showParticles ? Infinity : 0,
              repeatDelay: 2,
            }}
          />
        ))}
      </div>

      {/* Content layer - scrollable */}
      <div className="relative z-10 h-full overflow-y-auto overflow-x-hidden">
        {/* Main container with proper responsive padding */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Content sections with proper spacing */}
          <div className="flex flex-col items-center gap-6 sm:gap-8 lg:gap-12">
            {/* Champion section */}
            <section className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-8">
              {/* Avatar and crown group */}
              <motion.div
                className="relative flex flex-col items-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, type: "spring" }}
              >
                {/* Crown - always rendered, visibility controlled by opacity */}
                <motion.div
                  className="mb-2"
                  initial={{ scale: 0, y: 20, rotate: -180, opacity: 0 }}
                  animate={
                    showCrown
                      ? {
                          scale: 1,
                          y: 0,
                          rotate: 0,
                          opacity: 1,
                        }
                      : {
                          scale: 0,
                          y: 20,
                          rotate: -180,
                          opacity: 0,
                        }
                  }
                  transition={{
                    duration: 0.8,
                    type: "spring",
                    stiffness: 200,
                  }}
                >
                  <motion.div
                    animate={{
                      y: [0, -3, 0],
                      rotate: [-2, 2, -2],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="w-[3.5rem] h-[3rem] sm:w-[4.5rem] sm:h-[3.5rem] md:w-[5.5rem] md:h-[4rem] lg:w-[7rem] lg:h-[5rem] xl:w-[8rem] xl:h-[6rem]"
                  >
                    <svg
                      viewBox="0 0 100 75"
                      className="w-full h-full drop-shadow-[0_0.375rem_2.5rem_rgba(251,191,36,0.9)]"
                    >
                      <defs>
                        <linearGradient
                          id="crownGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#fbbf24" />
                          <stop offset="50%" stopColor="#f59e0b" />
                          <stop offset="100%" stopColor="#d97706" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M15 50 L25 25 L35 35 L50 12 L65 35 L75 25 L85 50 Z"
                        fill="url(#crownGradient)"
                        stroke="#fbbf24"
                        strokeWidth="2"
                      />
                      <circle
                        cx="25"
                        cy="25"
                        r="5"
                        fill="#fbbf24"
                        className="animate-pulse"
                      />
                      <circle
                        cx="50"
                        cy="12"
                        r="6"
                        fill="#f59e0b"
                        className="animate-pulse"
                        style={{ animationDelay: "0.5s" }}
                      />
                      <circle
                        cx="75"
                        cy="25"
                        r="5"
                        fill="#fbbf24"
                        className="animate-pulse"
                        style={{ animationDelay: "1s" }}
                      />
                    </svg>
                  </motion.div>
                </motion.div>

                {/* Avatar with glow effect */}
                <motion.div
                  className="relative"
                  animate={
                    showCrown
                      ? {
                          y: [0, -5, 0],
                        }
                      : {}
                  }
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  {/* Glow effect behind avatar */}
                  <motion.div
                    className="absolute -inset-6 sm:-inset-8 md:-inset-10 lg:-inset-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: showCrown ? 1 : 0 }}
                    transition={{ duration: 1 }}
                  >
                    <motion.div
                      className="w-full h-full rounded-full blur-2xl sm:blur-3xl"
                      animate={{
                        background: [
                          "radial-gradient(circle, rgba(251,191,36,0.3) 0%, transparent 60%)",
                          "radial-gradient(circle, rgba(245,158,11,0.5) 0%, transparent 60%)",
                          "radial-gradient(circle, rgba(251,191,36,0.3) 0%, transparent 60%)",
                        ],
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                  </motion.div>

                  {/* Avatar image */}
                  <div className="relative z-10">
                    <div className="w-[8rem] h-[8rem] sm:w-[10rem] sm:h-[10rem] md:w-[12rem] md:h-[12rem] lg:w-[14rem] lg:h-[14rem] xl:w-[16rem] xl:h-[16rem] rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl bg-gray-900">
                      <img
                        src={`/tokens/images/${winner.imagePath}`}
                        alt={winner.name}
                        className="w-full h-full object-cover"
                        loading="eager"
                      />
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Champion details */}
              <motion.div
                className="text-center max-w-2xl flex flex-col gap-2 sm:gap-3 lg:gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                {/* Name with gradient effect */}
                <motion.div
                  className="relative overflow-visible"
                  animate={{
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <h2
                    className="text-[1.5rem] sm:text-[1.75rem] md:text-[2.25rem] lg:text-[2.75rem] xl:text-[3.25rem] font-black bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent tracking-wide pb-1 sm:pb-2"
                    style={{
                      backgroundSize: "200% auto",
                      textShadow: "0 0 2.5rem rgba(251, 191, 36, 0.5)",
                    }}
                  >
                    {winner.name}
                  </h2>
                </motion.div>

                {/* Decorative divider */}
                <div className="flex items-center justify-center gap-2 sm:gap-3 py-1 sm:py-2">
                  <div className="h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent w-[3rem] sm:w-[4rem] lg:w-[5rem]" />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="text-amber-400"
                  >
                    ✦
                  </motion.div>
                  <div className="h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent w-[3rem] sm:w-[4rem] lg:w-[5rem]" />
                </div>

                {/* Symbol badge with winnings */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="inline-block mx-auto"
                >
                  <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 lg:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-amber-400/50 backdrop-blur-sm">
                    <span className="text-lg sm:text-xl lg:text-2xl font-bold text-amber-400">
                      {winner.symbol}
                    </span>
                    <div className="w-px h-[1.25rem] sm:h-[1.5rem] bg-amber-400/30" />
                    <motion.div
                      className="text-lg sm:text-xl lg:text-2xl font-black text-green-400"
                      animate={{
                        textShadow: [
                          "0 0 1.25rem rgba(74, 222, 128, 0.5)",
                          "0 0 2.5rem rgba(74, 222, 128, 0.8)",
                          "0 0 1.25rem rgba(74, 222, 128, 0.5)",
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      ${formatLiquidity(winner.liquidity + totalAbsorbed)}
                    </motion.div>
                  </div>
                </motion.div>

                {/* Description with quotes */}
                <div className="relative px-4 sm:px-6 py-2 sm:py-3 lg:py-4">
                  <div className="absolute top-0 left-0 text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] text-amber-400/20 font-serif">
                    "
                  </div>
                  <div className="absolute bottom-0 right-0 text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] text-amber-400/20 font-serif">
                    "
                  </div>
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg text-white/70 leading-relaxed italic font-light max-w-lg mx-auto">
                    {winner.description}
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Defeated tokens section */}
            {defeatedTokens.length > 0 && (
              <motion.section
                className="w-full max-w-5xl flex flex-col items-center"
                initial={{ opacity: 0, y: 30 }}
                animate={
                  showDefeated ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                }
                transition={{ duration: 0.8, type: "spring" }}
              >
                {/* Section header */}
                <header className="text-center mb-4 sm:mb-6 lg:mb-8">
                  <div className="text-sm sm:text-base lg:text-lg uppercase tracking-wider text-amber-400/80 font-semibold">
                    Path to Victory
                  </div>
                  <div className="text-xs sm:text-sm text-white/50 mt-1">
                    {defeatedTokens.length} Opponents Defeated
                  </div>
                </header>

                {/* Mobile: Grid layout */}
                <div className="sm:hidden w-full">
                  <div className="grid grid-cols-3 gap-2">
                    {defeatedTokens.map((defeated, index) => (
                      <motion.div
                        key={`${defeated.token.name}-${index}`}
                        className="flex flex-col items-center"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05, type: "spring" }}
                      >
                        <div className="text-[0.5rem] uppercase text-white/40 mb-0.5">
                          {defeated.round}
                        </div>
                        <div className="w-[2.5rem] h-[2.5rem] rounded-lg overflow-hidden opacity-80 grayscale bg-gray-800">
                          <img
                            src={`/tokens/images/${defeated.token.imagePath}`}
                            alt={defeated.token.name}
                            className="w-full h-full object-cover"
                            loading="eager"
                          />
                        </div>
                        <div className="text-[0.625rem] font-bold text-green-400/80 mt-0.5">
                          +${formatLiquidity(defeated.liquidityAbsorbed)}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Desktop: Flex layout with connectors */}
                <div className="hidden sm:flex flex-wrap justify-center gap-4 md:gap-6 lg:gap-8">
                  {defeatedTokens.map((defeated, index) => (
                    <motion.div
                      key={`${defeated.token.name}-${index}`}
                      className="relative group flex flex-col items-center"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05, type: "spring" }}
                      whileHover={{ scale: 1.15, zIndex: 10 }}
                    >
                      {/* Visual connector line */}
                      {index > 0 && (
                        <motion.div
                          className="absolute left-[-1rem] sm:left-[-1.5rem] md:left-[-2rem] top-8 sm:top-10 w-[1rem] sm:w-[1.5rem] md:w-[2rem] h-[2px]"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ delay: index * 0.05 + 0.2 }}
                        >
                          <div className="w-full h-full bg-gradient-to-r from-amber-400/20 to-amber-400/60" />
                        </motion.div>
                      )}

                      <div className="text-[0.625rem] sm:text-xs uppercase text-white/40 mb-1 sm:mb-2">
                        {defeated.round}
                      </div>
                      <div className="w-[3.5rem] h-[3.5rem] sm:w-[4rem] sm:h-[4rem] lg:w-[5rem] lg:h-[5rem] rounded-lg overflow-hidden opacity-80 grayscale hover:grayscale-0 hover:opacity-100 transition-all shadow-lg bg-gray-800">
                        <img
                          src={`/tokens/images/${defeated.token.imagePath}`}
                          alt={defeated.token.name}
                          className="w-full h-full object-cover"
                          loading="eager"
                        />
                      </div>
                      <div className="text-[0.625rem] sm:text-xs lg:text-sm font-bold text-green-400/80 mt-1 sm:mt-2">
                        +${formatLiquidity(defeated.liquidityAbsorbed)}
                      </div>

                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black/95 backdrop-blur border border-amber-400/30 rounded-lg text-sm text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-20 shadow-xl">
                        <div className="font-bold text-amber-400">
                          {defeated.token.name}
                        </div>
                        <div className="text-xs text-white/70">
                          {defeated.token.symbol}
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          {defeated.round}
                        </div>
                        <div className="text-sm font-bold text-green-400 mt-1">
                          +${formatLiquidity(defeated.liquidityAbsorbed)}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            )}

            {/* Return to Home Link */}
            <motion.section
              className="w-full flex flex-col items-center gap-4 mt-8 sm:mt-12 pb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 1 }}
            >
              <motion.button
                onClick={() => {
                  narrative.reset();
                  onReturnHome?.();
                }}
                className="group flex items-center gap-2 text-amber-400/80 hover:text-amber-300 transition-colors duration-300 px-6 py-3 -mx-6 -my-3"
                whileHover={{ x: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.span
                  animate={{ x: [0, -3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="text-lg"
                >
                  ←
                </motion.span>
                <span className="text-sm sm:text-base tracking-wider font-light">
                  Return to Home
                </span>
              </motion.button>
              
              {/* Contact Us Link */}
              <motion.a
                href="mailto:info@1v1me.me"
                className="text-white/60 hover:text-amber-400/80 transition-colors duration-300 text-sm sm:text-base font-light"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Contact Us
              </motion.a>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  );
};
